import React, { useEffect, useState } from "react";
import {
	Flex,
	Input,
	Button,
	Modal,
	ModalOverlay,
	ModalCloseButton,
	ModalContent,
	ModalBody,
	useDisclosure,
	useToast,
	Grid,
	GridItem,
	Select,
	Text,
	Stack,
} from "@chakra-ui/react";
import { useGetFamily } from "../../data/useGetFamily";
import { formatFechaDeNacimiento } from "../utils/utils";
import InputWrapper from "../utils/InputWrapper";
import DataValue from "../utils/DataValue";
import FileManagerModal from "../utils/FileManagerModal";
import {
	useDeleteFile,
	useFetchFile,
	useUploadFile,
} from "../../data/useFileManager";
import { optimizeImageFile } from "../utils/fileOptimization";

export default function FamilySection({
	userData,
	disableFields,
	setMainFormFamilyMembers,
	params,
	readOnly,
	isSupervisor,
	supervisorDNI,
}) {
	const { tokenSeguridad, id_persona } = userData;
	const { isOpen, onOpen, onClose } = useDisclosure();
	const {
		isOpen: currentFamiliarModalOpen,
		onOpen: openCurrentFamiliarModal,
		onClose: closeCurrentFamiliarModal,
	} = useDisclosure();
	const [dniFamiliar, setDniFamiliar] = useState("");
	const [selectedSupervisorFamiliar, setSelectedSupervisorFamiliar] =
		useState("");
	const [familyMembers, setFamilyMembers] = useState([]);
	const [selectedFamiliar, setSelectedFamiliar] = useState(null);

	const toast = useToast();

	const {
		mutate,
		isSuccess: familyFetched,
		data: familyData,
		isPending: fetching,
		reset: resetQuery,
	} = useGetFamily();

	const supervisorFamiliares = userData?.familiares || [];

	const triggerFamilySearch = (dniToSearch) => {
		if (!dniToSearch) {
			return;
		}

		mutate(
			{
				id_persona,
				dniFamiliar: dniToSearch,
				tokenSeguridad,
				supervisorDNI: supervisorDNI,
			},
			{
				onSuccess: () => {
					onOpen();
					setDniFamiliar("");
					setSelectedSupervisorFamiliar("");
				},
				onError: (err) => {
					var errorMessage =
						err?.response?.status === 404 && err?.response?.data?.observacion;

					toast({
						title:
							errorMessage || "No se encontró el familiar, comprueba el DNI",
						colorScheme: "red",
						position: "top-right",
					});
				},
			}
		);
	};

	const handleSearchFamiliar = () => {
		triggerFamilySearch(dniFamiliar);
	};

	const handleSupervisorSelection = (documento) => {
		setSelectedSupervisorFamiliar(documento);

		if (!documento) {
			return;
		}

		if (
			familyMembers.some((fm) => String(fm.documento) === String(documento))
		) {
			toast({
				title: "Este familiar ya está en la lista",
				colorScheme: "yellow",
				position: "top-right",
			});
			setSelectedSupervisorFamiliar("");
			return;
		}

		triggerFamilySearch(documento);
	};

	const handleAddFamiliar = (data) => {
		if (familyMembers.some((fm) => fm.documento === data.documento)) {
			return toast({
				title: "Este familiar ya se encuentra anotado",
				colorScheme: "yellow",
				position: "top-right",
			});
		}

		if (!data.nivel || !data.grado) {
			toast({
				title: "Completa el nivel y grado antes de agregar el familiar",
				colorScheme: "yellow",
				position: "top-right",
			});
			return;
		}
		console.log(data);

		setFamilyMembers((currentFamilyMembers) => [...currentFamilyMembers, data]);
		resetQuery();
		onClose();
	};

	const handleRemoveFamiliar = (data) => {
		const newList = familyMembers.filter((m) => m.documento !== data.documento);

		setFamilyMembers(newList);
		setSelectedFamiliar(null);
		closeCurrentFamiliarModal();
	};

	const handleUpdateFamiliarData = (updatedData) => {
		if (!updatedData?.documento) {
			return;
		}

		setFamilyMembers((currentFamilyMembers) =>
			currentFamilyMembers.map((member) =>
				member.documento === updatedData.documento
					? { ...member, ...updatedData }
					: member
			)
		);

		setSelectedFamiliar((currentSelected) =>
			currentSelected && currentSelected.documento === updatedData.documento
				? { ...currentSelected, ...updatedData }
				: currentSelected
		);
	};

	useEffect(() => {
		if (userData.inscripcion && userData.inscripcion.participantes) {
			const familyMembers = userData.inscripcion.participantes.filter(
				(fm) => fm.id_vinculo !== "1"
			);
			setFamilyMembers(familyMembers);
		}
	}, [userData.inscripcion]);

	const handleCurrentFamiliarModalOpen = (familiarData) => {
		setSelectedFamiliar(familiarData);
		openCurrentFamiliarModal();
	};

	useEffect(() => {
		setMainFormFamilyMembers(familyMembers);
	}, [familyMembers, setMainFormFamilyMembers]);

	return (
		<Flex w={["100%", "700px"]} direction={"column"}>
			<Stack
				direction={["column", "row"]}
				spacing={3}
				alignItems={"flex-end"}
				justifyContent={"space-between"}
				w="100%"
			>
				{isSupervisor ? (
					<InputWrapper label={"Selecciona un hijo/a"}>
						<Select
							placeholder={
								supervisorFamiliares.length === 0
									? "No hay familiares disponibles"
									: "Selecciona un hijo/a"
							}
							borderColor="#333"
							value={selectedSupervisorFamiliar}
							onChange={(event) =>
								handleSupervisorSelection(event.target.value)
							}
							fontWeight="600"
							bg={"#FAFAFA"}
							isDisabled={
								disableFields || fetching || supervisorFamiliares.length === 0
							}
						>
							{supervisorFamiliares.map((familiar) => (
								<option key={familiar.documento} value={familiar.documento}>
									{`${familiar.nombre} - ${familiar.documento}`}
								</option>
							))}
						</Select>
					</InputWrapper>
				) : (
					<>
						<InputWrapper label={"Dni Hijo/a"}>
							<Input
								borderColor="#333"
								type="number"
								value={dniFamiliar}
								onChange={(e) => setDniFamiliar(e.target.value)}
								fontWeight="600"
								placeholder={"Dni Hijo/a"}
								bg={"#FAFAFA"}
								sx={{
									_disabled: {
										opacity: 0.9,
									},
								}}
								disabled={disableFields}
							/>
						</InputWrapper>
						<Button
							colorScheme="yellow"
							_hover={{
								bg: "#ecc94b",
							}}
							onClick={handleSearchFamiliar}
							w={["100%", 200]}
							isLoading={fetching}
							isDisabled={disableFields || fetching || dniFamiliar === ""}
						>
							Buscar
						</Button>
					</>
				)}
				<InputWrapper label={"Kits"}>
					<Input
						borderColor="#333"
						value={familyMembers.length}
						isReadOnly
						fontWeight="600"
						bg={"#FAFAFA"}
					/>
				</InputWrapper>
			</Stack>

			<Flex
				w="100%"
				gap={1}
				direction={"column"}
				mt={3}
				bg="#b0b0b0"
				boxShadow={"0 0 5px 2px #999"}
			>
				<GridRow
					familyData={{
						nombre: "Nombre",
						curso: "Curso",
						ok: "OK",
					}}
					isHeader
				/>

				{familyMembers.map((fm) => {
					return (
						<GridRow
							familyData={fm}
							openModal={() => handleCurrentFamiliarModalOpen(fm)}
							disableFields={disableFields}
						/>
					);
				})}
			</Flex>

			<Modal
				isOpen={(isOpen && familyFetched) || currentFamiliarModalOpen}
				onClose={() => {
					onClose();
					closeCurrentFamiliarModal();
					setSelectedFamiliar(null);
				}}
			>
				<ModalOverlay />
				<ModalContent mx={5}>
					<ModalCloseButton />
					<ModalBody mt={6}>
						{(familyData || selectedFamiliar) && (
							<ConfirmFamily
								familyData={isOpen ? familyData?.familiar : selectedFamiliar}
								handleCancel={isOpen ? resetQuery : closeCurrentFamiliarModal}
								handleSubmitAction={
									isOpen ? handleAddFamiliar : handleRemoveFamiliar
								}
								isCurrent={currentFamiliarModalOpen}
								onUpdateFamiliar={handleUpdateFamiliarData}
								disableActions={disableFields}
								params={params}
								readOnly={readOnly}
							/>
						)}
					</ModalBody>
				</ModalContent>
			</Modal>
		</Flex>
	);
}

const ConfirmFamily = ({
	familyData,
	handleSubmitAction,
	handleCancel,
	isCurrent,
	onUpdateFamiliar,
	disableActions,
	params,
	readOnly,
}) => {
	const { nombre, nacimiento, vinculo, edad, documento } = familyData || {};
	const [nivel, setNivel] = useState(familyData?.nivel || "Inicial");
	const [grado, setGrado] = useState(familyData?.grado || "");
	const [certificate, setCertificate] = useState(
		familyData?.certificado || null
	);
	const {
		isOpen: certificateModalOpen,
		onOpen: openCertificateModal,
		onClose: closeCertificateModal,
	} = useDisclosure();
	const toast = useToast();
	const { mutate: uploadCertificate, isPending: isUploadingCertificate } =
		useUploadFile();
	const { mutate: deleteCertificate, isPending: isDeletingCertificate } =
		useDeleteFile();
	const { mutate: fetchCertificate } = useFetchFile();

	useEffect(() => {
		setNivel(familyData?.nivel || "");
		setGrado(familyData?.grado || "");
		setCertificate(familyData?.certificado || null);
	}, [familyData]);

	const handleNivelChange = (newNivel) => {
		setNivel(newNivel);
		if (newNivel != nivel) {
			setGrado("");
		}
		onUpdateFamiliar({
			...familyData,
			nivel: newNivel,
			grado: "",
		});
	};

	const handleGradoChange = (newGrado) => {
		setGrado(newGrado);
		onUpdateFamiliar({
			...familyData,
			grado: newGrado,
		});
	};

	useEffect(() => {
		if (!familyData?.documento) {
			return;
		}

		fetchCertificate(
			{
				type: "certificado",
				identifier: familyData.documento,
			},
			{
				onSuccess: (response) => {
					if (response?.file) {
						setCertificate(response.file.name);
						if (isCurrent && onUpdateFamiliar && familyData) {
							onUpdateFamiliar({
								...familyData,
								certificado: response.file,
							});
						}
					}
				},
			}
		);
	}, [familyData, fetchCertificate, isCurrent, onUpdateFamiliar]);

	if (!familyData) {
		return null;
	}

	const handleSubmit = () => {
		if (isCurrent) {
			handleSubmitAction(familyData);
			return;
		}

		handleSubmitAction({
			...familyData,
			nivel,
			grado,
			certificado: certificate,
			ok: certificate ? " " : "**",
		});
	};

	const handleCertificateUpload = async (file) => {
		if (!documento) {
			toast({
				title: "No se pudo identificar al hijo/a.",
				status: "warning",
				position: "top-right",
			});
			return;
		}

		const optimizedFile = await optimizeImageFile(file);
		const formData = new FormData();
		formData.append("action", "upload");
		formData.append("type", "certificado");
		formData.append("identifier", documento);
		formData.append("file", optimizedFile);

		uploadCertificate(formData, {
			onSuccess: (response) => {
				if (response?.file) {
					setCertificate(response.file.name);
					if (onUpdateFamiliar) {
						onUpdateFamiliar({
							...familyData,
							nivel,
							grado,
							certificado: response.file,
							ok: " ",
						});
					}
				}
				toast({
					title: "Certificado cargado correctamente.",
					status: "success",
					position: "top-right",
				});
				closeCertificateModal();
			},
			onError: () => {
				toast({
					title: "No se pudo cargar el certificado.",
					status: "error",
					position: "top-right",
				});
			},
		});
	};

	const handleCertificateDelete = () => {
		if (!documento) {
			return;
		}

		const formData = new FormData();
		formData.append("action", "delete");
		formData.append("type", "certificado");
		formData.append("identifier", documento);

		deleteCertificate(formData, {
			onSuccess: () => {
				setCertificate(null);
				if (onUpdateFamiliar) {
					onUpdateFamiliar({
						...familyData,
						certificado: null,
						ok: "**",
					});
				}
				toast({
					title: "Certificado eliminado.",
					status: "info",
					position: "top-right",
				});
				closeCertificateModal();
			},
			onError: () => {
				toast({
					title: "No se pudo eliminar el certificado.",
					status: "error",
					position: "top-right",
				});
			},
		});
	};

	const handleCertificateView = () => {
		if (!certificate) {
			return;
		}

		if (certificate?.url) {
			window.open(certificate.url, "_blank", "noopener,noreferrer");
		} else {
			window.open(
				"api/documentos/certificado/" + certificate,
				"_blank",
				"noopener,noreferrer"
			);
		}
	};

	const canModifyCertificate = !disableActions;

	const gradoOptions = params.cursos.grado[nivel] || [];

	return (
		<Flex direction={"column"} gap={4}>
			<DataValue data={"Nombre"} value={nombre} />
			<DataValue data={"DNI"} value={documento} />
			<DataValue data={"Vínculo"} value={vinculo} />
			<DataValue data={"Edad"} value={edad} />
			<DataValue
				data={"F. Nacimiento"}
				value={formatFechaDeNacimiento(nacimiento)}
			/>
			<Flex gap={3} direction={["column", "row"]}>
				<InputWrapper label={"Nivel"}>
					<Select
						placeholder="Selecciona un nivel"
						value={nivel}
						onChange={(event) => handleNivelChange(event.target.value)}
						isDisabled={disableActions}
						bg="#FAFAFA"
						borderColor="#333"
					>
						{params.cursos.nivel.map((option) => (
							<option key={option} value={option}>
								{option}
							</option>
						))}
					</Select>
				</InputWrapper>
				<InputWrapper label={"Año"}>
					<Select
						placeholder="Selecciona un año"
						value={grado}
						onChange={(event) => handleGradoChange(event.target.value)}
						isDisabled={disableActions}
						bg="#FAFAFA"
						borderColor="#333"
					>
						{gradoOptions.map((option) => (
							<option key={option} value={option}>
								{option}
							</option>
						))}
					</Select>
				</InputWrapper>
			</Flex>
			<Flex direction="column" gap={2}>
				<Button
					leftIcon={
						<Text as="span" fontSize="lg">
							📎
						</Text>
					}
					colorScheme="yellow"
					onClick={openCertificateModal}
					isDisabled={!documento}
				>
					Certificado
				</Button>
				<Text fontSize="sm" color="gray.600">
					{certificate
						? `Archivo: ${certificate.name || certificate}`
						: "Sin certificado cargado."}
				</Text>
			</Flex>
			{!readOnly && (
				<Flex w={["100%", 400]} gap={3} mt={2}>
					<Button colorScheme="gray" onClick={handleCancel} w={"100%"}>
						{isCurrent ? "Mantener" : "Cancelar"}
					</Button>
					<Button
						colorScheme={!isCurrent ? "yellow" : "red"}
						_hover={
							!isCurrent && {
								bg: "#ecc94b",
							}
						}
						onClick={handleSubmit}
						w={"100%"}
					>
						{isCurrent ? "Eliminar" : "Agregar"}
					</Button>
				</Flex>
			)}

			<FileManagerModal
				isOpen={certificateModalOpen}
				onClose={closeCertificateModal}
				title="Certificado"
				description="Subí el certificado en formato PDF o imagen."
				hasFile={Boolean(certificate)}
				onUpload={handleCertificateUpload}
				onView={handleCertificateView}
				onDelete={handleCertificateDelete}
				isUploading={isUploadingCertificate}
				isDeleting={isDeletingCertificate}
				uploadLabel="Subir certificado"
				viewLabel="Ver certificado"
				deleteLabel="Eliminar certificado"
				allowUpload={canModifyCertificate}
				allowDelete={canModifyCertificate}
			/>
		</Flex>
	);
};

const GridRow = ({ familyData, isHeader, openModal }) => {
	const curso = isHeader ? familyData.curso : buildCursoLabel(familyData);
	const okValue = familyData.ok;

	return (
		<Grid
			templateColumns={"2fr 1fr 40px"}
			gap={1}
			onClick={isHeader ? null : openModal}
			cursor={isHeader ? "default" : "pointer"}
		>
			<GridItem
				w="100%"
				h={"auto"}
				bg={isHeader ? "#444" : "#fafafa"}
				color={isHeader ? "#fafafa" : "#111"}
				alignItems={"center"}
				p="2px 5px"
				fontWeight={isHeader ? "700" : "500"}
			>
				{familyData.nombre}
			</GridItem>
			<GridItem
				w="100%"
				h={"auto"}
				bg={isHeader ? "#444" : "#fafafa"}
				color={isHeader ? "#fafafa" : "#111"}
				alignItems={"center"}
				p="2px 5px"
				fontWeight={isHeader ? "700" : "500"}
			>
				{curso}
			</GridItem>
			<GridItem
				w="40px"
				h={"auto"}
				bg={isHeader ? "#444" : "#fafafa"}
				color={isHeader ? "#fafafa" : "#111"}
				alignItems={"center"}
				p="2px 5px"
				fontWeight={isHeader ? "700" : "500"}
			>
				{okValue}
			</GridItem>
		</Grid>
	);
};

const buildCursoLabel = (familyData) => {
	const level = familyData?.nivel || "";
	const year = familyData?.grado || "";
	const curso = familyData?.curso;

	if (curso) {
		return curso;
	}

	const values = [level, year].filter((value) => value && value !== "-");
	return values.length > 0 ? values.join(" - ") : "-";
};
